/**
 * WatiAdminActions — extracted Iter 156.9 from `SettingsWatiCard.jsx`.
 *
 * The 6-button operational cluster: sync contacts, sync messages, refresh
 * all statuses, merge duplicates, re-route open Sales leads, re-classify
 * broadcasts, and repair broken media URLs. Each button has its own
 * loading + result panel. Purely presentational — all handlers come from
 * SettingsPage via props (same explicit-props pattern as iter 138).
 */
import { Loader2, RefreshCw, MessageSquare, Users, GitBranch } from "lucide-react";

export default function WatiAdminActions({
  watiStatus,
  syncing, handleSyncWati,
  syncingMessages, handleSyncMessages,
  handleRefreshAllWatiStatuses, refreshingStatuses, statusRefreshProgress,
  dupeCheckResult, mergingDupes, handleCheckDuplicates, handleMergeDuplicates,
  reroutingAll, handleRerouteAll, rerouteResult,
  reclassifyingBc, handleReclassifyBroadcasts, reclassifyResult,
  repairingMedia, handleRepairMedia, mediaRepairResult,
}) {
  const previewDisabled = !!watiStatus?.is_preview;
  return (
    <div className="space-y-2" data-testid="wati-admin-actions">
      <button
        onClick={handleSyncWati}
        disabled={syncing || previewDisabled}
        title={previewDisabled ? "Disabled in preview — use production environment" : ""}
        className="btn-secondary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="sync-wati-btn"
      >
        {syncing ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
        {syncing ? "Syncing Contacts..." : previewDisabled ? "Sync Contacts (disabled in preview)" : "Sync Contacts"}
      </button>

      <button
        onClick={handleSyncMessages}
        disabled={syncingMessages || previewDisabled}
        title={previewDisabled ? "Disabled in preview — use production environment" : ""}
        className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="sync-messages-btn"
      >
        {syncingMessages ? <Loader2 size={18} className="animate-spin" /> : <MessageSquare size={18} />}
        {syncingMessages ? "Syncing Messages..." : previewDisabled ? "Sync Message History (disabled in preview)" : "Sync Message History"}
      </button>

      <button
        onClick={handleRefreshAllWatiStatuses}
        disabled={refreshingStatuses || previewDisabled}
        title={previewDisabled ? "Disabled in preview — use production" : "One-time pull of ticket status (OPEN/PENDING/SOLVED) for ALL leads from WATI"}
        className="btn-secondary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed border-amber-300"
        data-testid="refresh-all-wati-btn"
      >
        {refreshingStatuses ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
        {refreshingStatuses
          ? `Refreshing… ${statusRefreshProgress?.leads_updated || 0} updated / ${statusRefreshProgress?.contacts_scanned || 0} scanned`
          : previewDisabled ? "Refresh All WATI Statuses (disabled in preview)" : "Refresh All WATI Statuses"}
      </button>
      {statusRefreshProgress?.status === "done" && (
        <div className="p-2 bg-emerald-50 border border-emerald-200 rounded text-[11px] text-emerald-800" data-testid="status-refresh-result">
          ✓ Done: {statusRefreshProgress.leads_updated} of {statusRefreshProgress.contacts_scanned} leads re-aligned with WATI
          {statusRefreshProgress.stale_auto_closed > 0 && ` · ${statusRefreshProgress.stale_auto_closed} stale leads auto-closed (>30d)`}
          {statusRefreshProgress.errors > 0 && ` · ${statusRefreshProgress.errors} errors`}
        </div>
      )}

      {/* Duplicate-lead merge — fixes AI replying to one copy while agent sees another */}
      <button
        onClick={dupeCheckResult && dupeCheckResult.duplicate_phone_count > 0 ? handleMergeDuplicates : handleCheckDuplicates}
        disabled={mergingDupes}
        title="Find and merge duplicate leads (same phone stored as +91XXX and 91XXX). Fixes the AI-replies-to-duplicate bug."
        className="btn-secondary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed border-amber-300"
        data-testid="merge-duplicates-btn"
      >
        {mergingDupes ? <Loader2 size={16} className="animate-spin" /> : <Users size={16} />}
        {mergingDupes
          ? "Working…"
          : dupeCheckResult && dupeCheckResult.duplicate_phone_count > 0
            ? `Merge ${dupeCheckResult.duplicate_phone_count} Duplicate Phones (${dupeCheckResult.extra_rows_to_delete} extra rows)`
            : dupeCheckResult && dupeCheckResult.duplicate_phone_count === 0
              ? "✓ No Duplicates Found (click to re-check)"
              : "Check for Duplicate Leads"}
      </button>
      {dupeCheckResult && dupeCheckResult.duplicate_phone_count > 0 && dupeCheckResult.sample_first_10 && (
        <div className="p-2 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-800 font-mono max-h-32 overflow-y-auto" data-testid="dupe-sample">
          Sample (first 10):
          {dupeCheckResult.sample_first_10.map((d) => (
            <div key={d.phone}>• {d.phone} — {d.count} copies</div>
          ))}
        </div>
      )}

      {/* Bulk re-route — re-runs the CS-intent classifier on all open Sales leads. */}
      <button
        onClick={handleRerouteAll}
        disabled={reroutingAll}
        title="Re-run the Customer Support classifier on all currently-open Sales leads (scans last 5 customer messages each). Never closes/solves tickets."
        className="btn-secondary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed border-purple-300"
        data-testid="reroute-all-open-btn"
      >
        {reroutingAll ? <Loader2 size={16} className="animate-spin" /> : <GitBranch size={16} />}
        {reroutingAll ? "Re-routing…" : "Re-route All Open Sales Leads"}
      </button>
      {rerouteResult && (
        <div className="p-2 bg-purple-50 border border-purple-200 rounded text-[11px] text-purple-900" data-testid="reroute-result">
          ✓ Scanned {rerouteResult.scanned} · {rerouteResult.rerouted} flipped to Customer Support
          {rerouteResult.errors > 0 && ` · ${rerouteResult.errors} errors`}
        </div>
      )}

      {/* Retroactive broadcast re-classifier — flips misrouted broadcasts + reverts wrongful auto-reopens. */}
      <button
        onClick={handleReclassifyBroadcasts}
        disabled={reclassifyingBc}
        title="Scan existing messages and re-classify broadcast/template bodies wrongly stored as incoming. Reverts auto-reopens caused by those messages."
        className="btn-secondary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed border-amber-300"
        data-testid="reclassify-broadcasts-btn"
      >
        {reclassifyingBc ? <Loader2 size={16} className="animate-spin" /> : <GitBranch size={16} />}
        {reclassifyingBc ? "Re-classifying…" : "Re-classify Broadcasts (retro fix)"}
      </button>
      {reclassifyResult && (
        <div className="p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-900" data-testid="reclassify-result">
          ✓ Scanned {reclassifyResult.scanned} · {reclassifyResult.flipped_to_broadcast} re-classified · {reclassifyResult.leads_reverted_to_closed} leads reverted to closed
        </div>
      )}

      {/* Repair broken media URLs (id-only WATI placeholders) */}
      <button
        onClick={handleRepairMedia}
        disabled={repairingMedia || previewDisabled}
        className="w-full px-3 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-[#D6D3D1] text-white rounded-md text-[12px] font-medium transition-all flex items-center justify-center gap-1.5"
        data-testid="repair-media-btn"
        title="Resolve broken media URLs (hex-id placeholders) by querying WATI getMessages"
      >
        {repairingMedia ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
        {repairingMedia ? "Repairing…" : "Repair Broken Media URLs"}
      </button>
      {mediaRepairResult && (
        <div className="p-2 bg-purple-50 border border-purple-200 rounded text-[11px] text-purple-900" data-testid="repair-media-result">
          ✓ Bare-paths wrapped: {mediaRepairResult.bare_path_fixed ?? 0} · Prefix added: {mediaRepairResult.prefix_fixed ?? 0} · Queued for WATI lookup: {mediaRepairResult.queued_for_background_lookup ?? 0}
        </div>
      )}
    </div>
  );
}
